//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// event-smart-hold-info imports
import { IContainer, IProps } from "./EventSmartHoldInfo.d";
import { eventSmartHoldInfoFragments } from "./EventSmartHoldInfo.gql";
import * as s from "./EventSmartHoldInfo.styles";
import { CalloutCard } from "@clockwise/web-commons/src/ui/callout-card";
// state
import { IReduxState } from "@reducers/root.reducer";
import {
  setActiveViewDay,
  setActiveViewFeed,
  setSettingsDialogOpen,
} from "@actions/chrome-extension.actions";
import { openSmartHolds } from "@actions/settings.actions";
// util
import { ZonedMoment } from "@util/time-zone.util";
import {
  addAndRemoveOrgTags,
  unsyncFocusTimeEvent as unsyncFocusTimeEventMutation,
  removeLunchHold,
  addAndRemoveUserTags,
} from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { track, TrackingEvents } from "@util/analytics.util";
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import {
  isLunchSmartHold,
  isFocusTimeSmartHold,
  isTravelTimeSmartHold,
  isClockwiseUnsyncedEvent,
  isSmartHoldPaused,
} from "@clockwise/client-commons/src/util/event";
import { createEventParentRelayIdFromOrgRelayId } from "@util/event.util";
import { Tags } from "@clockwise/client-commons/src/util/event-tag";
import { getAbsoluteTime } from "@clockwise/client-commons/src/util/format-time";
import { isShadowCalendarId } from "@util/calendar-id.util";
import { isMovementPaused } from "@util/event.util";

// material-ui
import { Button } from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import { RelayModernEnvironment } from "relay-runtime/lib/store/RelayModernEnvironment";

//////////////////
// COMPONENT
//////////////////

const EventSmartHoldInfoCmpt = (props: IProps) => {
  const {
    event,
    smartHoldToDeleteExternalEventId,
    eventDeleteButtonClicked,
    snackBarOpen,
    relay,
    org,
    setActiveViewFeed,
    smartHoldEventInfo,
  } = props;
  const [deleteDisabled, setDeleteDisabled] = React.useState(false);

  const getSmartHoldType = () => {
    const { org, event, calendarId } = props;

    let shadowCalendarId: string | null = null;
    let isShadowCalendar = false;
    if (
      org.shadowCalendarSettingsErrorable &&
      org.shadowCalendarSettingsErrorable.__typename === "ShadowCalendarSettings"
    ) {
      shadowCalendarId = org.shadowCalendarSettingsErrorable.shadowCalendarId;
      isShadowCalendar = isShadowCalendarId(calendarId, shadowCalendarId);
    }

    const isFocusTime = isFocusTimeSmartHold(event);
    const isLunch = isLunchSmartHold(event);
    const isTravelTime = isTravelTimeSmartHold(event);
    const clockwiseUnsyncedEvent = isClockwiseUnsyncedEvent(event, isShadowCalendar);

    return { isFocusTime, isLunch, isTravelTime, isShadowCalendar, clockwiseUnsyncedEvent };
  };

  const onRemoveLunchSuccess = () => {
    setDeleteDisabled(false);
    snackBarOpen(
      "Your lunch hold was removed. Please allow a few moments for the update to occur.",
    );
    setActiveViewFeed();
  };

  const onRemoveLunchFailure = () => {
    setDeleteDisabled(false);
    snackBarOpen("Sorry, there was a problem removing your lunch event");
  };

  const removeMovementPausedForEvent = (
    event: ISchema.IEvent,
    org: ISchema.IOrg,
    environment: RelayModernEnvironment,
  ) => {
    const eventParentRelayId = createEventParentRelayIdFromOrgRelayId(
      org.id,
      event.eventKey.externalEventId,
    );
    if (eventParentRelayId) {
      const input: ISchema.IAddAndRemoveOrgTagsMutationInput = {
        eventParentRelayId,
        orgRelayId: org.id,
        calendarId: (event.calendarIds && event.calendarIds[0]) || event.organizer?.urnValue,
        tagNamesToRemove: ["MovementPaused"],
      };

      return new Promise((resolve, reject) => {
        addAndRemoveOrgTags(environment, input, resolve, reject);
      });
    }
    return Promise.resolve(0);
  };

  const removeLunchEvent = () => {
    setDeleteDisabled(true);
    track(TrackingEvents.EVENT.REMOVE_LUNCH_EVENT);
    const lunchEventId = event.eventKey.externalEventId;
    const orgRelayId = org.id;
    const movementPaused = isMovementPaused(event);

    removeLunchHold(
      relay.environment,
      {
        lunchEventId,
        orgRelayId,
        isPaused: movementPaused,
        calendarId: org.primaryOrgCalendar,
      },
      onRemoveLunchSuccess,
      onRemoveLunchFailure,
    );

    if (movementPaused) {
      removeMovementPausedForEvent(event, org, relay.environment).catch(onRemoveLunchFailure);
    }
  };

  const onUnsyncSuccess = (movementPaused?: boolean) => {
    setDeleteDisabled(false);
    const startTime = event.startTime && new ZonedMoment(event.startTime.millis);
    const endTime = event.endTime && new ZonedMoment(event.endTime.millis);
    let msg = "This Focus Time event will be removed from your calendar";
    if (startTime && endTime) {
      const timeRange = getAbsoluteTime(startTime, endTime);
      if (movementPaused) {
        msg = `Clockwise will resume managing Focus Time from ${timeRange} on this day.`;
      } else {
        msg = `Clockwise will no longer hold Focus Time from ${timeRange} on this day.`;
      }
    }
    snackBarOpen(msg);
    setActiveViewFeed();
  };

  const onUnsyncFailure = () => {
    setDeleteDisabled(false);
    snackBarOpen("Sorry, there was a problem removing your Focus Time event from your calendar");
  };

  const unsyncFocusTimeEvent = () => {
    track(TrackingEvents.EVENT.UNSYNC_FOCUS_TIME_EVENT);
    setDeleteDisabled(true);
    const movementPaused = isMovementPaused(event);
    const input = {
      orgRelayId: org.id,
      timeRange: {
        id: `${event.startTime.millis};;${event.endTime.millis}`,
        startTime: event.startTime,
        endTime: event.endTime,
      },
      isPaused: movementPaused, // May be used in the near future, keeping as part of the mutation for now.
      email: org.primaryOrgEmail,
      calendarId: org.primaryOrgCalendar,
      externalEventId: event.eventKey.externalEventId,
    };

    unsyncFocusTimeEventMutation(
      relay.environment,
      input,
      onUnsyncSuccess.bind(null, movementPaused),
      onUnsyncFailure,
    );

    if (movementPaused) {
      removeMovementPausedForEvent(event, org, relay.environment).catch(onUnsyncFailure);
    }
  };

  const unsyncSmartHold = () => {
    const { isFocusTime, clockwiseUnsyncedEvent, isTravelTime, isLunch } = getSmartHoldType();

    // This splitting logic of unsyncing or removing should live on the webserver
    if (isFocusTime && clockwiseUnsyncedEvent) {
      snackBarOpen(
        "Focus Time on your Clockwise Private Calendar is just a suggestion (that is private), and cannot currently be deleted.",
      );
    } else if (isFocusTime && !clockwiseUnsyncedEvent) {
      unsyncFocusTimeEvent();
    } else if (isTravelTime) {
      removeTravelTimeEvent();
    } else if (isLunch) {
      removeLunchEvent();
    }
  };

  const onRemoveTravelTimeFailure = () => {
    setDeleteDisabled(false);
    snackBarOpen("Sorry, there was a problem removing your travel time event from your calendar");
  };

  const onRemoveTravelTimeSuccess = () => {
    setDeleteDisabled(false);
    let msg = "This travel time event will be removed from your calendar";
    if (
      smartHoldEventInfo &&
      smartHoldEventInfo.originEvent &&
      smartHoldEventInfo.originEvent.title
    ) {
      const title = smartHoldEventInfo.originEvent.title;
      const beforeAfter = smartHoldEventInfo.isPreTravelTime ? "before" : "after";
      msg = `Clockwise will no longer hold travel time ${beforeAfter} "${title}".`;
    }
    msg += " It may take a few minutes for changes to take effect.";
    snackBarOpen(msg);
    setActiveViewFeed();
  };

  const removeTravelTimeEvent = () => {
    if (!smartHoldEventInfo || !smartHoldEventInfo.originEvent) {
      onRemoveTravelTimeFailure();
      return;
    }

    const eventParentRelayId = createEventParentRelayIdFromOrgRelayId(
      org.id,
      smartHoldEventInfo.originEvent.eventKey.externalEventId,
    );
    if (!eventParentRelayId) {
      onRemoveTravelTimeFailure();
      return;
    }

    setDeleteDisabled(true);
    const tagName = smartHoldEventInfo.isPreTravelTime ? Tags.PreTravelTime : Tags.PostTravelTime;
    const overrideTagName = smartHoldEventInfo.isPreTravelTime
      ? Tags.PreTravelTimeManualOverride
      : Tags.PostTravelTimeManualOverride;
    const input = {
      orgRelayId: org.id,
      eventParentRelayId,
      tagNamesToRemove: [tagName],
      tagsToAdd: [
        {
          name: Tags.TravelTimeManualOverride,
        },
        {
          name: overrideTagName,
        },
      ],
    };

    addAndRemoveUserTags(
      relay.environment,
      input,
      onRemoveTravelTimeSuccess,
      onRemoveTravelTimeFailure,
    );

    track(TrackingEvents.EVENT.REMOVE_TRAVEL_TIME_EVENT);
  };

  // UNSAFE_componentWillReceiveProps
  React.useEffect(() => {
    const externalEventId = event?.eventKey?.externalEventId;

    if (externalEventId && smartHoldToDeleteExternalEventId === externalEventId) {
      unsyncSmartHold();
    }
  }, [eventDeleteButtonClicked]); // eventDeleteButtonClicked is set by other components

  const isPaused = isSmartHoldPaused(event.annotatedEvent);
  const {
    isFocusTime,
    isLunch,
    isTravelTime,
    isShadowCalendar,
    clockwiseUnsyncedEvent,
  } = getSmartHoldType();

  const isYourCalendar =
    (event.organizer &&
      event.organizer.urnValue &&
      event.organizer.urnValue === org.primaryOrgCalendar) ||
    isShadowCalendar;

  let smartHoldDescription;
  if (isTravelTime) {
    smartHoldDescription = <>This event can be edited or removed.&nbsp;</>;
  } else if (isPaused) {
    smartHoldDescription = <>The timing of this event is set, because it was edited.</>;
  } else {
    smartHoldDescription = (
      <>The timing and duration of this event may update as your schedule changes.&nbsp;</>
    );
  }

  let smartHoldLabel = "event",
    smartHoldLearnMore = "Learn more",
    smartHoldUrlByType,
    smartHoldHeader,
    onSmartHoldLearnMore;

  const onLearnMoreTravelTime = () => {
    track(TrackingEvents.HELP.TRAVEL_TIME);
  };

  const onLearnMoreLunchSync = () => {
    track(TrackingEvents.HELP.LUNCH_SYNC);
  };

  const onLearnMoreFocusTimeSync = () => {
    track(TrackingEvents.HELP.FOCUS_TIME_SYNC);
  };

  if (isTravelTime) {
    smartHoldHeader = "Travel Time";
    smartHoldUrlByType = articleUrls.travelTime;
    smartHoldLearnMore += " about travel time holds";
    onSmartHoldLearnMore = onLearnMoreTravelTime;
    smartHoldLabel = "travel time event";
  } else if (isLunch) {
    smartHoldHeader = "Lunch";
    smartHoldUrlByType = articleUrls.lunchSync;
    smartHoldLearnMore += " about lunch holds";
    onSmartHoldLearnMore = onLearnMoreLunchSync;
    smartHoldLabel = "lunch event";
  } else if (isFocusTime) {
    smartHoldHeader = "Focus Time";
    smartHoldUrlByType = articleUrls.focusTime;
    smartHoldLearnMore += " about Focus Time";
    onSmartHoldLearnMore = onLearnMoreFocusTimeSync;
    smartHoldLabel = "Focus Time";
  }

  return (
    <div>
      {isYourCalendar && (
        <CalloutCard
          sentiment="info"
          content={{
            header: smartHoldHeader,
            body: (
              <>
                <p>This {smartHoldLabel} was automatically created by Clockwise.</p>
                <p className="cw-mt-1">{smartHoldDescription}</p>
                <div className="cw-mt-1">
                  <a
                    target="_blank"
                    href={smartHoldUrlByType}
                    onClick={onSmartHoldLearnMore}
                    className={isPaused ? "text-peach-darker" : "text-green-darker"}
                  >
                    {smartHoldLearnMore}
                  </a>
                </div>
              </>
            ),
          }}
        />
      )}
      {!clockwiseUnsyncedEvent && isYourCalendar && (
        <Button
          className="cw-mt-4 cw-text-slate-standard"
          variant="outlined"
          disabled={deleteDisabled}
          onClick={unsyncSmartHold}
          fullWidth
        >
          <Delete style={s.flatButtonIcon} />
          Remove this hold
        </Button>
      )}
    </div>
  );
};

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    eventDeleteButtonClicked: state.webExtension.eventDeleteButtonClicked,
    smartHoldToDeleteExternalEventId: state.webExtension.smartHoldToDeleteExternalEventId,
  };
}

const EventSmartHoldInfoRedux = connect(mapStateToProps, {
  snackBarOpen,
  setActiveViewDay,
  setActiveViewFeed,
  setSettingsDialogOpen,
  openSmartHolds,
})(EventSmartHoldInfoCmpt);

//////////////////
// RELAY
//////////////////
export const EventSmartHoldInfo = createFragmentContainer<IContainer>(
  EventSmartHoldInfoRedux,
  eventSmartHoldInfoFragments,
);
